import type { BlockConfig, RoutablePageConfig } from '@kizenapps/engine';

export const CUSTOM_CODE_KIND = 'custom_code' as const;
export const CUSTOM_CODE_CONTENT_VERSION = 1 as const;
export const CUSTOM_CODE_PLUGIN_PREFIX = 'custom_block:';

const STATIC_CONTENT_CHART_TYPE = 'html';

export type CustomCodeEventScript = {
  name: string;
  script: string;
};

// A plugin view the block can open with this.showViewInModal(apiName). Wire
// shape (snake_case, as the CLI PATCHes it) is api_name/event_scripts; the app
// sees it camelCased by DashletService. event_scripts is an array, never a
// map, because the key conversion would rewrite map keys.
export type CustomCodeView = {
  apiName: string;
  name?: string;
  type: 'script' | 'html';
  script?: string;
  html?: string;
  css?: string;
  eventScripts: CustomCodeEventScript[];
};

export type CustomCodeContent = {
  kind: typeof CUSTOM_CODE_KIND;
  version: typeof CUSTOM_CODE_CONTENT_VERSION;
  name: string;
  script: string;
  styles: string;
  eventScripts: CustomCodeEventScript[];
  minW?: number;
  maxW?: number;
  minH?: number;
  maxH?: number;
  defaultW?: number;
  defaultH?: number;
  hostChrome?: boolean;
  views?: CustomCodeView[];
};

export type CustomCodeParseError =
  | 'empty'
  | 'invalid_json'
  | 'not_an_object'
  | 'missing_script'
  | 'invalid_name'
  | 'invalid_styles'
  | 'invalid_event_scripts'
  | 'invalid_dimensions'
  | 'invalid_default_dimensions'
  | 'invalid_host_chrome'
  | 'invalid_views'
  | 'unsupported_content';

export type CustomCodeParseResult =
  | { ok: true; content: CustomCodeContent }
  | { ok: false; error: CustomCodeParseError };

type DashletLike = {
  config?: { chartType?: string; content?: unknown } | null;
} | null;

const DIMENSION_KEYS = [
  ['min_w', 'minW'],
  ['max_w', 'maxW'],
  ['min_h', 'minH'],
  ['max_h', 'maxH'],
] as const;

const DEFAULT_DIMENSION_KEYS = [
  ['default_w', 'defaultW', 'minW', 'maxW'],
  ['default_h', 'defaultH', 'minH', 'maxH'],
] as const;

type DimensionKey = 'minW' | 'maxW' | 'minH' | 'maxH' | 'defaultW' | 'defaultH';

const UNSUPPORTED_SEQUENCES = ['\u0000', '{__ref:'];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isPositiveInteger = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value > 0;

export const isCustomCodeContent = (
  content: unknown
): content is CustomCodeContent =>
  isRecord(content) && content.kind === CUSTOM_CODE_KIND;

export const isHostChromeDisabled = (content: unknown): boolean =>
  isCustomCodeContent(content) && content.hostChrome === false;

export const isCustomCodeDashlet = (dashlet?: DashletLike): boolean =>
  dashlet?.config?.chartType === STATIC_CONTENT_CHART_TYPE &&
  isCustomCodeContent(dashlet.config.content);

const hasUnsupportedContent = (values: string[]) =>
  values.some((value) =>
    UNSUPPORTED_SEQUENCES.some((sequence) => value.includes(sequence))
  );

const eventScriptsToRecord = (eventScripts: CustomCodeEventScript[]) =>
  Object.fromEntries(eventScripts.map(({ name, script }) => [name, script]));

const parseEventScripts = (
  value: unknown
): CustomCodeEventScript[] | undefined => {
  if (value === undefined) {
    return [];
  }
  if (!isRecord(value)) {
    return undefined;
  }
  const entries = Object.entries(value);
  if (entries.some(([, script]) => typeof script !== 'string')) {
    return undefined;
  }
  return entries.map(([name, script]) => ({ name, script: script as string }));
};

const VIEW_TYPES: readonly CustomCodeView['type'][] = ['script', 'html'];

const OPTIONAL_VIEW_TEXT_KEYS = ['name', 'script', 'html', 'css'] as const;

// Reads one view in either key style: snake_case in the editor JSON (the wire
// shape) or camelCase once DashletService has converted it. Only the keys the
// source carries are copied, so a round trip reproduces the view exactly.
const readView = (
  value: unknown,
  keys: { apiName: string; eventScripts: string }
): CustomCodeView | undefined => {
  if (!isRecord(value)) {
    return undefined;
  }
  const apiName = value[keys.apiName];
  const { type } = value;
  const eventScripts = value[keys.eventScripts];
  if (
    typeof apiName !== 'string' ||
    !VIEW_TYPES.includes(type as CustomCodeView['type']) ||
    !Array.isArray(eventScripts)
  ) {
    return undefined;
  }
  const view: CustomCodeView = {
    apiName,
    type: type as CustomCodeView['type'],
    eventScripts: [],
  };
  for (const key of OPTIONAL_VIEW_TEXT_KEYS) {
    const text = value[key];
    if (text === undefined) {
      continue;
    }
    if (typeof text !== 'string') {
      return undefined;
    }
    view[key] = text;
  }
  // Event script names must be unique: the engine takes them as a map
  const names = new Set<string>();
  for (const eventScript of eventScripts) {
    if (
      !isRecord(eventScript) ||
      typeof eventScript.name !== 'string' ||
      !eventScript.name ||
      typeof eventScript.script !== 'string' ||
      names.has(eventScript.name)
    ) {
      return undefined;
    }
    names.add(eventScript.name);
    view.eventScripts.push({
      name: eventScript.name,
      script: eventScript.script,
    });
  }
  return view;
};

const readViews = (
  value: unknown,
  keys: { apiName: string; eventScripts: string }
): CustomCodeView[] | undefined => {
  if (!Array.isArray(value)) {
    return undefined;
  }
  // Duplicate api_names are kept as received (two packaged views can
  // sanitize to the same name); the modal lookup resolves the first
  const views: CustomCodeView[] = [];
  for (const item of value) {
    const view = readView(item, keys);
    if (!view) {
      return undefined;
    }
    views.push(view);
  }
  return views;
};

const SNAKE_VIEW_KEYS = { apiName: 'api_name', eventScripts: 'event_scripts' };
const CAMEL_VIEW_KEYS = { apiName: 'apiName', eventScripts: 'eventScripts' };

const serializeView = (view: CustomCodeView) => {
  const block: Record<string, unknown> = { api_name: view.apiName };
  if (view.name !== undefined) {
    block.name = view.name;
  }
  block.type = view.type;
  for (const key of ['script', 'html', 'css'] as const) {
    if (view[key] !== undefined) {
      block[key] = view[key];
    }
  }
  block.event_scripts = view.eventScripts.map(({ name, script }) => ({
    name,
    script,
  }));
  return block;
};

export const parseCustomCodeBlock = (text: string): CustomCodeParseResult => {
  if (!text.trim()) {
    return { ok: false, error: 'empty' };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { ok: false, error: 'invalid_json' };
  }

  if (!isRecord(parsed)) {
    return { ok: false, error: 'not_an_object' };
  }

  const { name, script, styles, event_scripts } = parsed;

  if (typeof script !== 'string' || !script.trim()) {
    return { ok: false, error: 'missing_script' };
  }
  if (name !== undefined && typeof name !== 'string') {
    return { ok: false, error: 'invalid_name' };
  }
  if (styles !== undefined && typeof styles !== 'string') {
    return { ok: false, error: 'invalid_styles' };
  }

  const eventScripts = parseEventScripts(event_scripts);
  if (!eventScripts) {
    return { ok: false, error: 'invalid_event_scripts' };
  }

  if (
    hasUnsupportedContent([
      name ?? '',
      script,
      styles ?? '',
      ...eventScripts.flatMap((eventScript) => [
        eventScript.name,
        eventScript.script,
      ]),
    ])
  ) {
    return { ok: false, error: 'unsupported_content' };
  }

  const dimensions: Partial<Pick<CustomCodeContent, DimensionKey>> = {};
  for (const [sourceKey, targetKey] of DIMENSION_KEYS) {
    const value = parsed[sourceKey];
    if (value === undefined) {
      continue;
    }
    if (!isPositiveInteger(value)) {
      return { ok: false, error: 'invalid_dimensions' };
    }
    dimensions[targetKey] = value;
  }

  if (
    (dimensions.minW !== undefined &&
      dimensions.maxW !== undefined &&
      dimensions.minW > dimensions.maxW) ||
    (dimensions.minH !== undefined &&
      dimensions.maxH !== undefined &&
      dimensions.minH > dimensions.maxH)
  ) {
    return { ok: false, error: 'invalid_dimensions' };
  }

  for (const [sourceKey, targetKey, minKey, maxKey] of DEFAULT_DIMENSION_KEYS) {
    const value = parsed[sourceKey];
    if (value === undefined) {
      continue;
    }
    const min = dimensions[minKey];
    const max = dimensions[maxKey];
    if (
      !isPositiveInteger(value) ||
      (min !== undefined && value < min) ||
      (max !== undefined && value > max)
    ) {
      return { ok: false, error: 'invalid_default_dimensions' };
    }
    dimensions[targetKey] = value;
  }

  const { host_chrome } = parsed;
  if (host_chrome !== undefined && typeof host_chrome !== 'boolean') {
    return { ok: false, error: 'invalid_host_chrome' };
  }

  let views: CustomCodeView[] | undefined;
  if (parsed.views !== undefined) {
    views = readViews(parsed.views, SNAKE_VIEW_KEYS);
    // No unsupported-sequence check here: app-builder doesn't apply one to
    // views, and a stricter editor would refuse to re-save pushed content
    if (!views) {
      return { ok: false, error: 'invalid_views' };
    }
  }

  return {
    ok: true,
    content: {
      kind: CUSTOM_CODE_KIND,
      version: CUSTOM_CODE_CONTENT_VERSION,
      name: name?.trim() ?? '',
      script,
      styles: styles ?? '',
      eventScripts,
      ...dimensions,
      ...(host_chrome !== undefined && { hostChrome: host_chrome }),
      ...(views !== undefined && { views }),
    },
  };
};

export const serializeCustomCodeBlock = (
  content: CustomCodeContent
): string => {
  const block: Record<string, unknown> = {
    name: content.name,
    script: content.script,
    styles: content.styles,
    event_scripts: eventScriptsToRecord(content.eventScripts),
  };
  for (const [sourceKey, targetKey] of [
    ...DIMENSION_KEYS,
    ...DEFAULT_DIMENSION_KEYS,
  ]) {
    if (content[targetKey] !== undefined) {
      block[sourceKey] = content[targetKey];
    }
  }
  if (content.hostChrome !== undefined) {
    block.host_chrome = content.hostChrome;
  }
  if (content.views !== undefined) {
    // Server content is unvalidated (isCustomCodeContent checks only kind).
    // Malformed views go into the text as received, so the editor still
    // opens and parse reports invalid_views for the user to fix.
    const views = readViews(content.views, CAMEL_VIEW_KEYS);
    block.views = views ? views.map(serializeView) : content.views;
  }
  return JSON.stringify(block, null, 2);
};

// content is passed through untouched (views included), so edits and
// duplicates never drop fields this module doesn't rebuild.
export const buildCustomCodeConfig = ({
  content,
  feExtraInfo = {},
}: {
  content: CustomCodeContent;
  feExtraInfo?: Record<string, unknown>;
}) => ({
  entity_type: 'static_content',
  report_type: 'html',
  chart_type: STATIC_CONTENT_CHART_TYPE,
  content,
  fe_extra_info: feExtraInfo,
});

export const toEngineBlockConfig = (
  content: CustomCodeContent,
  dashletId: string
): BlockConfig => ({
  plugin_api_name: `${CUSTOM_CODE_PLUGIN_PREFIX}${dashletId}`,
  api_name: dashletId,
  name: content.name,
  type: 'script',
  script: content.script,
  styles: content.styles,
  event_scripts: eventScriptsToRecord(content.eventScripts),
});

// The block's views as the routable pages PluginView looks up, under the same
// plugin_api_name the block's own worker runs as (the engine relays
// showViewInModal as {viewId, pluginApiName}). Content arrives from the server
// unvalidated, so a malformed views value yields no pages instead of throwing.
export const toEngineViewConfigs = (
  content: CustomCodeContent,
  dashletId: string
): RoutablePageConfig[] => {
  if (content.views === undefined) {
    return [];
  }
  const views = readViews(content.views, CAMEL_VIEW_KEYS) ?? [];
  return views.map((view) => ({
    plugin_api_name: `${CUSTOM_CODE_PLUGIN_PREFIX}${dashletId}`,
    api_name: view.apiName,
    name: view.name ?? view.apiName,
    type: view.type,
    ...(view.script !== undefined && { script: view.script }),
    ...(view.html !== undefined && { html: view.html }),
    ...(view.css !== undefined && { css: view.css }),
    event_scripts: eventScriptsToRecord(view.eventScripts),
  }));
};
